import React, { useEffect, useState } from "react";
import {
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
  Label,
  Input,
  Row,
  Col,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import Icon from "Components/Icons/Icon";
import { QuoteListFilterState } from "./QuoteList";
import { ErrorToast } from "utils/toast";

interface QuoteListFilterProps {
  filters: QuoteListFilterState;
  show: boolean;
  onCloseClick: () => void;
  onFilterChange: (filters: {
    startDate: string | null;
    endDate: string | null;
    status: string[];
  }) => void;
}

const STATUS_GROUPS = [
  ["draft", "completed"],
  ["sent", "approved"],
  ["paid", "rejected"],
  ["cancelled"],
];

const QuoteListFilter: React.FC<QuoteListFilterProps> = ({
  filters,
  show,
  onCloseClick,
  onFilterChange,
}) => {
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string[]>([]);

  useEffect(() => {
    setStartDate(filters.startDate);
    setEndDate(filters.endDate);
    setSelectedStatus(filters.status);
  }, [filters]);

  const handleStatusChange = (status: string) => {
    setSelectedStatus((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    console.log(startDate, endDate);
    if (!startDate && !endDate && selectedStatus.length === 0) {
      ErrorToast("Please select at least one filter option.");
      return;
    }
    onFilterChange({ startDate, endDate, status: selectedStatus });
  };

  const handleClear = () => {
    setStartDate(null);
    setEndDate(null);
    setSelectedStatus([]);
    onFilterChange({ startDate: null, endDate: null, status: [] });
  };

  const handleCalendarIconClick = (inputId: string) => {
    const input = document.getElementById(inputId) as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const renderStatusGroup = (group: string[], rowIndex: number) => (
    <Row className="g-2 d-flex" key={rowIndex}>
      {group.map((status, colIndex) => (
        <Col key={colIndex}>
          <Input
            className="form-check-input me-2"
            type="checkbox"
            checked={selectedStatus.includes(status)}
            onChange={() => handleStatusChange(status)}
            id={`${status}-${rowIndex}-${colIndex}`}
            style={{ width: 20, height: 20 }}
          />
          <Label
            className="text-black2D font-primary fw-normal fs-16 text-capitalize"
            htmlFor={`${status}-${rowIndex}-${colIndex}`}
          >
            {status}
          </Label>
        </Col>
      ))}
    </Row>
  );

  return (
    <Offcanvas direction="end" isOpen={show} toggle={onCloseClick}>
      <OffcanvasHeader className="bg-greyF5" toggle={onCloseClick}>
        <div className="text-black2D font-primary fw-medium fs-16">Filters</div>
      </OffcanvasHeader>

      <form className="d-flex flex-column justify-content-end h-100">
        <OffcanvasBody className="pt-3 mt-2">
          {/* DATE RANGE */}
          <Label className="text-grey89 font-primary fw-medium fs-14 ">
            DATE RANGE
          </Label>

          <Row className="g-2 align-items-center mb-4 mt-1">
            {/* START DATE */}
            <Col md={6} lg={6}>
              <Label className="text-black2D font-primary fw-normal fs-14">
                Start Date
              </Label>
              <div className="position-relative">
                <Flatpickr
                  id="start-date-picker"
                  value={startDate ? new Date(startDate) : undefined}
                  className="form-control br-7 fw-normal fs-16"
                  placeholder="Start Date"
                  options={{
                    maxDate: endDate ? new Date(endDate) : undefined,
                    onChange: (selectedDates, dateStr) => {
                      setStartDate(dateStr || null);
                    },
                  }}
                />
                <Icon
                  name="Calendar"
                  className="position-absolute"
                  style={{
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleCalendarIconClick("start-date-picker")}
                />
              </div>
            </Col>

            {/* END DATE */}
            <Col md={6} lg={6}>
              <Label className="text-black2D font-primary fw-normal fs-14">
                End Date
              </Label>
              <div className="position-relative">
                <Flatpickr
                  id="end-date-picker"
                  value={endDate ? new Date(endDate) : undefined}
                  className="form-control br-7 fw-normal fs-16"
                  placeholder="End Date"
                  options={{
                    minDate: startDate ? new Date(startDate) : undefined,
                    onChange: (selectedDates, dateStr) => {
                      setEndDate(dateStr || null);
                    },
                  }}
                />
                <Icon
                  name="Calendar"
                  className="position-absolute"
                  style={{
                    right: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                  }}
                  onClick={() => handleCalendarIconClick("end-date-picker")}
                />
              </div>
            </Col>
          </Row>

          {/* STATUS */}

          <Label className="text-grey89 font-primary fw-medium fs-14">
            STATUS
          </Label>

          <Row className="g-2">
            {STATUS_GROUPS.map((group, index) =>
              renderStatusGroup(group, index)
            )}
          </Row>
        </OffcanvasBody>

        {/* FOOTER */}
        <div className="offcanvas-footer border-top p-3 text-center hstack gap-2">
          <button
            type="button"
            className="btn bg-greyF5 text-black2D fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleClear}
          >
            Clear
          </button>

          <button
            type="submit"
            className="btn bg-primary text-white fs-14 fw-normal font-primary w-100 br-7"
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};

export default QuoteListFilter;
